import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { verifyEmail, resendVerification } from '../services/api';

/**
 * Email verification screen. Reached right after registration (the account is
 * created + logged in but unverified). Verifying the 6-digit code is required
 * before the user can fund their wallet or place bets.
 */
export default function VerifyEmailPage() {
  const { user, fetchUser } = useAuth();
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  // Already verified? Nothing to do here.
  useEffect(() => {
    if (user?.email_verified) {
      navigate('/wallet', { replace: true });
    }
  }, [user?.email_verified, navigate]);

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setSubmitting(true);
    try {
      await verifyEmail(code);
      await fetchUser(); // refresh email_verified in context
      navigate('/wallet', { replace: true });
    } catch (err: unknown) {
      const anyErr = err as { response?: { status?: number; data?: { detail?: unknown } } };
      const detail = anyErr?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Verification failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setInfo(null);
    setResending(true);
    try {
      const res = await resendVerification();
      setInfo(res.data.detail || 'A new code is on its way.');
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { detail?: unknown } } };
      const detail = anyErr?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not resend the code. Try again shortly.');
    } finally {
      setResending(false);
    }
  };

  const inputClass =
    'w-full min-h-[44px] rounded-button border border-border bg-surface px-4 py-3 text-center font-mono text-2xl tracking-[0.5em] text-text-primary placeholder-text-muted outline-none focus:border-primary focus:ring-2 focus:ring-primary/20';

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md">
        <h1 className="mb-2 text-center font-heading text-3xl font-bold text-primary">
          ✉️ Verify your email
        </h1>
        <p className="mb-6 text-center text-sm text-text-muted">
          We sent a 6-digit code to{' '}
          <span className="font-semibold text-text-secondary">{user?.email ?? 'your email'}</span>.
          Enter it below to activate your account. You&apos;ll need to verify before you can
          deposit or bet.
        </p>

        <form onSubmit={handleVerify} className="space-y-4" noValidate>
          <input
            id="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className={inputClass}
            placeholder="••••••"
            aria-label="6-digit verification code"
          />

          {error && (
            <div className="rounded-button bg-error/10 px-4 py-3 text-sm text-error" role="alert">
              {error}
            </div>
          )}
          {info && (
            <div className="rounded-button bg-success/10 px-4 py-3 text-sm text-success" role="status">
              {info}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="cursor-pointer min-h-[44px] w-full rounded-button bg-primary py-3 font-bold text-white shadow-btn transition-colors hover:bg-primary-dark disabled:opacity-60"
          >
            {submitting ? 'Verifying…' : 'Verify my email'}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-text-muted">
          Didn&apos;t get it?{' '}
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="font-semibold text-primary hover:underline disabled:opacity-60"
          >
            {resending ? 'Sending…' : 'Resend code'}
          </button>
        </div>

        <p className="mt-6 text-center text-xs text-text-muted">
          Check your spam folder if it doesn&apos;t arrive.{' '}
          <Link to="/" className="font-semibold text-primary hover:underline">
            Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}
